import { Fragment, useState } from 'react'
import { api } from '../api/client'
import { useLoadable, ErrorBlock } from '../components/ui'
import type { AdoptionDrive, AdoptionPeriod, AdoptionReport, AdoptionTeam } from '../types'

/* Adoption dashboard v1 — project team only (allowlist enforced by the API).
   Portal visitors only for now; filing, moves and attendance are added once their
   sources are verified (GLB_RMS_AdoptionDashboard_Design_20261001.md).
   Unknown is never zero: days before tracking began say so instead of showing 0.
   Teams keep a fixed order; this is not a league table. */

const PERIODS: { key: AdoptionPeriod; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: 'since', label: 'Since tracking began' },
]

const day = (d: string, year = true) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' })

const eatTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Dar_es_Salaam' })

function periodLine(r: AdoptionReport): string {
  const range = r.period.start === r.period.end ? day(r.period.start) : `${day(r.period.start, false)} – ${day(r.period.end)}`
  if (r.period.key === 'today') return `${range} · midnight to now, EAT · provisional`
  if (r.period.key === '7d') return `${range} · seven completed days, EAT`
  return `${range} · EAT · today provisional`
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="stat">
      <p className="stat__label">{label}</p>
      <p className="stat__value">{value}</p>
      <p className="stat__sub">{sub}</p>
    </div>
  )
}

const driveLoaded = (d: AdoptionDrive) => d.coverage === 'full' || d.coverage === 'partial'
const driveSub = (d: AdoptionDrive, loaded: string) =>
  d.coverage === 'pending' ? 'Pending next refresh (03:30 EAT)' : d.coverage === 'not_started' ? 'Not yet loaded' : loaded
const driveRange = (d: AdoptionDrive) =>
  d.observedFrom && d.observedThrough ? (d.observedFrom === d.observedThrough ? day(d.observedFrom) : `${day(d.observedFrom, false)} – ${day(d.observedThrough)}`) : ''

function TeamRows({ t, tracked, filing }: { t: AdoptionTeam; tracked: boolean; filing: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <Fragment>
      <tr>
        <td>
          <button className="linkbtn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {t.team} {open ? '↑' : '→'}
          </button>
        </td>
        <td className="num">{t.eligible}</td>
        <td className="num">{!tracked ? '—' : t.eligible ? `${t.visitors} of ${t.eligible}` : t.visitors}</td>
        <td className="num">{filing ? t.activeFilers : '—'}</td>
      </tr>
      {open && t.departments.map((d) => (
        <tr key={d.department} className="subrow">
          <td style={{ paddingLeft: 'var(--s-xl)' }}>{d.department}</td>
          <td className="num">{d.eligible}</td>
          <td className="num">{tracked ? `${d.visitors} of ${d.eligible}` : '—'}</td>
          <td className="num">{filing ? d.activeFilers : '—'}</td>
        </tr>
      ))}
    </Fragment>
  )
}

export default function AdoptionRoute() {
  const [period, setPeriod] = useState<AdoptionPeriod>('7d')
  const [rep, retry] = useLoadable<AdoptionReport>(() => api.getAdoption(period), () => false, [period])

  const r = rep.status === 'ready' ? rep.data : null
  const tracked = !!r && r.coverage !== 'not_started'
  const filing = !!r && driveLoaded(r.drive)

  return (
    <>
      <p className="eyebrow">Adoption · project team</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>RMS adoption</h1>
      <p className="note" style={{ marginBottom: 'var(--s-md)' }}>
        Who is using the portal and filing in RMS, by home team (Group or OpCo) and department. A visit is one
        signed-in person on one day (EAT). An active new filer is a person who created or uploaded a new
        file in one of the 12 RMS drives; copies made for migration do not count. Files brought into RMS
        counts individual files moved in from outside RMS, plus bulk uploads (one person uploading more than
        20 files in a day: existing files, not new work); folders moved whole are listed separately, without
        counting what is inside them. Drive activity loads nightly. Counts only: no names.
      </p>

      <div className="opts" role="group" aria-label="Reporting period" style={{ marginBottom: 'var(--s-xs)' }}>
        {PERIODS.map((p) => (
          <button key={p.key} className="opt opt--neutral" aria-pressed={period === p.key} onClick={() => setPeriod(p.key)}>{p.label}</button>
        ))}
      </div>
      <p className="note" style={{ marginBottom: 'var(--s-md)', minHeight: '1.4em' }}>{r ? periodLine(r) : ' '}</p>

      {rep.status === 'error' && <ErrorBlock message={rep.message} correlationId={rep.correlationId} onRetry={retry} />}

      {rep.status === 'loading' && (
        <div aria-busy="true">
          <div className="skeleton" style={{ height: '7rem', marginBottom: 12 }} />
          <div className="skeleton" style={{ height: '14rem' }} />
        </div>
      )}

      {r && (
        <>
          {r.coverage === 'not_started' && (
            <div className="block block--sunk" style={{ marginBottom: 'var(--s-md)' }}>
              <h3>Not yet tracked</h3>
              <p>
                {r.tracking.trackingStartedAt
                  ? <>Portal visits are recorded from {eatTime(r.tracking.trackingStartedAt)} EAT.</>
                  : <>No portal visit has been recorded in this environment yet.</>}
              </p>
            </div>
          )}
          {r.coverage === 'partial' && r.observedFrom && (
            <p className="block" style={{ marginBottom: 'var(--s-md)' }}>
              Observed from {day(r.observedFrom)}, when visit tracking began. Earlier days in this period were not tracked.
            </p>
          )}

          <div className="stats" style={{ marginBottom: 'var(--s-xl)' }}>
            <Stat label="Portal visitors" value={r.totals ? r.totals.visitors.toLocaleString() : '—'}
                  sub={r.totals ? 'Distinct signed-in people' : 'Not yet tracked'} />
            <Stat label="Visits" value={r.totals ? r.totals.visitorDays.toLocaleString() : '—'}
                  sub={r.totals ? 'Person-days with a visit' : 'Not yet tracked'} />
            <Stat label="Active new filers" value={filing ? r.drive.activeFilers.toLocaleString() : '—'}
                  sub={driveSub(r.drive, `People creating files in RMS · ${driveRange(r.drive)}`)} />
            <Stat label="Files brought into RMS" value={filing ? r.drive.movedFiles.toLocaleString() : '—'}
                  sub={driveSub(r.drive, `Moved in or bulk uploaded${r.drive.bulkUploadedFiles ? ` (${r.drive.bulkUploadedFiles.toLocaleString()} uploaded)` : ''} · ${driveRange(r.drive)}`)} />
            <Stat label="Staff in rollout" value={r.cohort ? r.cohort.eligible.toLocaleString() : '—'}
                  sub={r.cohort ? `Training roster, ${day(r.cohort.asOf)}` : 'Roster not loaded'} />
          </div>

          {filing && (
            <div style={{ marginTop: 'calc(-1 * var(--s-lg))', marginBottom: 'var(--s-xl)' }}>
              {(() => {
                const hub = r.drive.byDrive.find((d) => d.drive === '00 RMS Company Hub')
                return hub && (
                  <p className="note" style={{ marginBottom: 'var(--s-xs)' }}>
                    <b>Company Hub</b> · {hub.moved} files brought in · {hub.created} new files · {driveRange(r.drive)}. The Hub has no staff of its own, so it appears only as a destination.
                  </p>
                )
              })()}
              {(r.drive.foldersMovedIn > 0 || r.drive.movedUnattributed > 0) && (
                <p className="note" style={{ marginBottom: 'var(--s-xs)' }}>
                  Not in the moved-files count: {r.drive.foldersMovedIn} folders moved in whole (contents not yet counted) and{' '}
                  {r.drive.movedUnattributed} moves into RMS whose origin could not be confirmed.
                </p>
              )}
              {r.drive.missingDates.length > 0 && (
                <p className="note" style={{ marginBottom: 0 }}>
                  <b>Incomplete:</b> Drive activity for {r.drive.missingDates.map((d) => day(d, false)).join(', ')} is not loaded yet, so these figures are a minimum.
                </p>
              )}
            </div>
          )}

          <h2>People by home team</h2>
          <p className="note" style={{ marginBottom: 'var(--s-sm)' }}>
            Home team is each person's RMS home drive (Group, Global Partnerships or their country) from the training roster. Open a team for its departments.
            People who visited but are not on the roster are counted under <b>Not on roster</b>.
          </p>
          <div className="tablewrap" style={{ marginBottom: 'var(--s-xl)' }}>
            <table>
              <thead><tr><th>Home team</th><th style={{ textAlign: 'right' }}>Staff in rollout</th><th style={{ textAlign: 'right' }}>Portal visitors</th><th style={{ textAlign: 'right' }}>Active new filers</th></tr></thead>
              <tbody>
                {r.teams.map((t) => <TeamRows key={t.team} t={t} tracked={tracked} filing={filing} />)}
                <tr>
                  <td>Not on roster</td>
                  <td className="num">—</td>
                  <td className="num">{tracked ? r.unassigned : '—'}</td>
                  <td className="num">{filing ? r.drive.filersNotOnRoster : '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {filing && (
            <>
              <h2>By destination drive</h2>
              <p className="note" style={{ marginBottom: 'var(--s-sm)' }}>
                Where files landed, {driveRange(r.drive)}. A move's origin is confirmed from where its source folder is today.
                Department-level destinations come next.
              </p>
              <div className="tablewrap" style={{ marginBottom: 'var(--s-xl)' }}>
                <table style={{ minWidth: 360 }}>
                  <thead><tr><th>RMS drive</th><th style={{ textAlign: 'right' }}>Files brought in</th><th style={{ textAlign: 'right' }}>New files</th></tr></thead>
                  <tbody>
                    {r.drive.byDrive.map((d) => <tr key={d.drive}><td>{d.drive}</td><td className="num">{d.moved}</td><td className="num">{d.created}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {r.daily.length > 0 && (
            <>
              <h2>By day</h2>
              <div className="tablewrap">
                <table style={{ minWidth: 320 }}>
                  <thead><tr><th>Date (EAT)</th><th style={{ textAlign: 'right' }}>Portal visitors</th></tr></thead>
                  <tbody>
                    {[...r.daily].reverse().map((d) => (
                      <tr key={d.date}><td>{day(d.date)}</td><td className="num">{d.visitors}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}
